<template>
  <section>
    <Tabs>
      <TabsHeader tabs-header-class="atb-bg-white">
        <Tab
          v-for="(tab, index) in tabs"
          :key="tab.id"
          :index="index"
          :value="selectedTab"
          :closable="tab.name !== 'All Attributes'"
          @tabClick="(index: number) => changeTab(index)"
          @close="(index: number) => closeTab(index, tab)"
        >
          {{ tab.name }}
          <sup v-if="getTabDirtyStatus(tab.id)">*</sup>
        </Tab>
        <confirmation-popup
          v-if="displayConfirmationPopup"
          :index="closeTabIndex"
          @closeActionPopup="displayConfirmationPopup = false"
          @closeConfirmationIndex="(index: number) => removeTab(index, tabs[index])"
        />
      </TabsHeader>
      <TabsBody>
        <TabPanelItem
          v-for="(tab, index) in tabs"
          :key="tab.id"
        >
          <keep-alive
            :key="index"
            v-if="index === 0"
          >
            <AttributesList
              ref="attributesListRef"
              :key="tab.id"
              v-show="selectedTab === index"
              :client-id="clientId"
              :attribute-id="tab.id"
              @create-new-tab="createNewTab"
              @create="createNewAttribute"
              @copy="copyAttribute"
              @delete="deleteModalRef?.changeModalVisibility()"
              @show-dependency="
                attributesDependencyModalRef?.changeModalVisibility()
              "
            />
          </keep-alive>
          <keep-alive
            :key="tab.id"
            v-else
          >
            <AttributeDetails
              :key="tab.id"
              v-show="selectedTab === index"
              :current-client-id="clientId"
              :current-attribute-id="tab.id"
              :tab-name="tab.name"
              @create-new-tab="createNewTab"
              @refresh-attributes-after-delete="refreshAttributesAfterDelete"
            />
          </keep-alive>
        </TabPanelItem>
      </TabsBody>
    </Tabs>
    <AttributeDelete
      @delete="deleteAttribute"
      :is-used="isAttributeUsed"
      :entity="'Attributes'"
      ref="deleteModalRef"
    />
    <AttributesDependency
      @delete="deleteAttribute"
      :mass-delete-dependencies="massDeleteDependencies"
      ref="attributesDependencyModalRef"
    />
  </section>
</template>

<script lang="ts" setup>
  import { nextTick, Ref, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import Tabs from '@atb-ui/eds-vue/src/components/Tabs/Tabs.vue';
  import Tab from '@atb-ui/eds-vue/src/components/Tabs/Tab.vue';
  import TabsHeader from '@atb-ui/eds-vue/src/components/Tabs/TabsHeader.vue';
  import TabsBody from '@atb-ui/eds-vue/src/components/Tabs/TabsBody.vue';
  import TabPanelItem from '@atb-ui/eds-vue/src/components/Tabs/TabPanelItem.vue';
  import AttributeDetails from '@/app/features/ams/attributes/components/AttributesDetails.vue';
  import AttributesList from '@/app/features/ams/attributes/components/AttributesList.vue';
  import ConfirmationPopup from '@/app/shared/components/ConfirmationPopup.vue';
  import attributesApi from '@/app/features/ams/attributes/attributes-service';
  import AttributeDelete from '@/app/features/ams/shared/DeleteModal.vue';
  import AttributesDependency from '@/app/features/ams/shared/EntityDependency.vue';
  import { Attribute } from '@/app/shared/types/models/attribute.interface';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { useAttributesStore } from '@/app/features/ams/attributes/attributes-store';
  import { AddTabEvent } from '@/app/shared/types/events/add-tab-event.interface';
  import { TabHeader } from '@/app/shared/types/tab-header.interface';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useI18n } from 'vue-i18n';
  import { Motif } from '@/app/shared/enums/motif-enum';
  import { ModalActions } from '@/app/shared/types/modal-actions.interface';
  import { ViewCodeMetaInformation } from '@/app/shared/types/models/view-code-meta-information.interface';

  const { t } = useI18n();
  const attributesStore = useAttributesStore();
  const clientStore = useClientStore();
  const alertStore = useAlertStore();

  const deleteModalRef: Ref<ModalActions | null> = ref(null);
  const attributesDependencyModalRef: Ref<ModalActions | null> = ref(null);
  // TODO: Create an interface for this attribute or use an existing one
  const attributesListRef = ref(null) as Ref<any>;
  const closeTabIndex = ref(-1);
  const displayConfirmationPopup = ref(false);
  const clientId = clientStore.selectedClient.id;
  const { selectedTab, massDeleteDependencies, deleteEntityIds, tabs } =
    storeToRefs(attributesStore);

  const getTabDirtyStatus = (attributeId: number) => {
    let dirtyFlag = false;
    let dirtyTab = attributesStore.getAttributeById(attributeId)?.dirtyTab;
    if (typeof dirtyTab !== 'undefined') {
      for (const [, dirtyTabValue] of dirtyTab ?? new Map().entries()) {
        if (dirtyTabValue) {
          dirtyFlag = true;
        }
      }
    }
    return dirtyFlag;
  };

  const changeTab = (index: number) => {
    selectedTab.value = index;
    if (selectedTab.value === 0) {
      attributesListRef.value[0].refreshAttributes();
    }
  };
  // TODO: Create an interface for this attribute or use an existing one
  const closeTab = (index: number, tab: any) => {
    closeTabIndex.value = index;
    if (getTabDirtyStatus(tab.id)) {
      displayConfirmationPopup.value = true;
    } else {
      displayConfirmationPopup.value = false;
      removeTab(index, tab);
    }
  };

  const removeTab = async (index: number, { id }: TabHeader) => {
    displayConfirmationPopup.value = false;
    attributesStore.removeTab(id);
    const isTabSaved = !isNaN(id) && id !== -1;
    try {
      isTabSaved && (await attributesApi.unlockAttribute(clientId, id, true));
    } catch (e) {
      displayError(e);
    }
    attributesStore.updateAutoLockInfoInOverviewList(clientId, id, null, null);
    attributesListRef.value[0]?.refreshAttributes();

    //if tab is closed, select prev tab
    if (index === 1 && index <= selectedTab.value) {
      selectedTab.value -= 1;
      nextTick(() => {
        attributesListRef.value[0].refreshAttributes();
      });
    } else if (index <= selectedTab.value) {
      selectedTab.value -= 1;
      attributesListRef.value[0].refreshAttributes();
    }
  };

  const createNewTab = async ({
    clientId,
    id,
    name,
    isNew,
    dirtyTab,
  }: AddTabEvent): Promise<void> => {
    const tabIndex = tabs.value.findIndex(
      (tab: TabHeader) => tab.name === name
    );
    if (tabIndex === -1) {
      await attributesStore.createAttributeTab(
        clientId,
        Number(id),
        name,
        isNew,
        dirtyTab
      );
      selectedTab.value = tabs.value.findIndex(
        (tab: TabHeader) => tab.name === name
      );
    } else {
      selectedTab.value = tabIndex;
    }
  };
  // TODO: Create an interface for this attribute or use an existing one
  const displayError = (error: any) => {
    alertStore.showNotification(
      error.errors[0].message,
      t('attributes'),
      Motif.ERROR
    );
  };

  const copyAttribute = async ({
    id,
    length,
    defaultValue,
    format,
    description,
    initializationValue,
    codeType,
    longName,
    isStagg,
    systems,
  }: ViewCodeMetaInformation) => {
    await attributesStore.createNewAttributeTab(
      clientId,
      {
        id: -1,
        name: '',
        isNew: true,
        dirtyTab: new Map<string, boolean>(),
        isCopied: {
          id: id,
          isCopy: true,
        },
      },
      {
        length,
        defaultValue,
        format,
        description,
        initializationValue,
        isStagg,
        codeType,
        longName,
        shortName: '',
        clientId: clientId,
        isVisible: true,
        isPrimary: false,
        imported: false,
        versionControls: [],
        systems,
      }
    );
  };

  const createNewAttribute = async () => {
    await attributesStore.createNewAttributeTab(
      clientId,
      {
        id: -1,
        name: '',
        isNew: true,
        dirtyTab: new Map<string, boolean>(),
        isCopied: {
          isCopy: false,
          id: -1,
        },
      },
      {
        length: 1,
        defaultValue: '',
        format: 'Alphanumeric',
        shortName: '',
        description: '',
        initializationValue: '',
        clientId: clientId,
        codeType: 'Attribute',
        longName: '',
        isVisible: true,
        isPrimary: false,
        imported: false,
        isStagg: false,
        versionControls: [],
        systems: [],
      }
    );
    selectedTab.value = tabs.value.length - 1;
  };

  const isAttributeUsed = () => {
    const selectedAttributes: Array<Array<string>> | undefined =
      attributesListRef.value[0].gridApi
        ?.getSelectedRows()
        .map((attribute: Attribute) => attribute.inuseBy);
    return selectedAttributes
      ?.map((coincidences) => {
        return coincidences?.length > 0;
      })
      .find((validations) => validations === true);
  };

  const refreshAttributesAfterDelete = () => {
    selectedTab.value = 0;
    nextTick(() => {
      attributesListRef.value[0]?.refresh();
    });
  };

  const deleteAttribute = async () => {
    try {
      const selectedAttributes = attributesListRef.value[0].gridApi
        ?.getSelectedRows()
        .map((attribute: Attribute) => attribute.id);

      await attributesStore.deleteAttributes(
        clientId,
        deleteEntityIds.value ?? selectedAttributes
      );
      alertStore.showNotification(
        t('attributesDeleted'),
        t('attributes'),
        Motif.SUCCESS
      );
      attributesListRef.value[0].gridApi?.deselectAll();
      attributesListRef.value[0]?.refreshAttributes();
    } catch (e) {
      alertStore.showNotification(
        t('deleteError'),
        t('attributes'),
        Motif.ERROR
      );
    }
  };
</script>
